
import React from 'react';
import { Edit, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { getUnitLabel } from '@/types/unit-types';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';

interface LineItem {
  id: string;
  item_type?: 'internal' | 'system';
  name?: string;
  title?: string;
  description?: string;
  base_unit: string;
  base_cost: number;
  conversion_unit?: string;
  conversion_factor?: number;
  productivity_unit?: string;
  productivity_rate?: number;
  tags?: string[] | string;
  image_urls?: string[];
  category?: string;
  category_name?: string | null;
  trade?: string;
  trade_name?: string | null;
  browse_category_id?: string | null;
  browse_category_name?: string | null;
  internal_pricing_tiers?: PricingTier[];
  markup_percentage?: number;
  markup_source?: string;
  final_price?: number;
  override_markup?: boolean;
  custom_markup?: number;
  notes?: string | null;
  default_markup_percent?: number | null;
  price_last_updated_at?: string | null;
  unit_symbol?: string | null;
  updated_at?: string;
}

interface PricingTier {
  id: string;
  line_item_id: string;
  qty_min: number;
  qty_max?: number;
  unit_cost: number;
}

interface LineItemDetailsDialogProps {
  selectedItem: LineItem | null;
  onClose: () => void;
  onEdit: (id: string) => void;
  onToggleSystemOverride?: () => void;
  onSaveSystemOverride?: () => void;
  onClearSystemOverride?: () => void;
  systemOverrideMarkup?: number;
  setSystemOverrideMarkup?: (value: number) => void;
  lastSeenPrice?: number | null;
  hideMarkupSource?: boolean;
  priceSourcesLabel?: string;
}

const LineItemDetailsDialog: React.FC<LineItemDetailsDialogProps> = ({
  selectedItem,
  onClose,
  onEdit,
  onToggleSystemOverride,
  onSaveSystemOverride,
  onClearSystemOverride,
  systemOverrideMarkup,
  setSystemOverrideMarkup,
  lastSeenPrice,
  hideMarkupSource,
  priceSourcesLabel,
}) => {
  const navigate = useNavigate();

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.style.display = 'none';
    e.currentTarget.parentElement?.classList.add('no-image');
  };

  const renderTags = (tags?: string[] | string) => {
    if (!tags) return null;
    
    let tagsArray: string[] = [];
    
    if (Array.isArray(tags)) {
      tagsArray = tags;
    } else if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) {
          tagsArray = parsed;
        } else {
          tagsArray = [tags];
        }
      } catch (e) {
        tagsArray = [tags];
      }
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {tagsArray.map((tag, index) => (
          <Badge key={index} variant="outline">{tag}</Badge>
        ))}
      </div>
    );
  };

  const getPricingSourceBadgeVariant = (
    source?: string,
    isOverride?: boolean
  ): "default" | "secondary" | "outline" | "destructive" => {
    if (isOverride) return 'default';
    switch (source) {
      case 'custom':
      case 'item_override': return 'default';
      case 'category':
      case 'company_category_tier': return 'secondary';
      case 'global':
      case 'company_default': return 'outline';
      case 'system_default_tier': return 'outline';
      case 'system_default': return 'outline';
      default: return 'outline';
    }
  };

  const getPricingSourceLabel = (
    source?: string,
    isOverride?: boolean,
    customMarkup?: number
  ) => {
    if (isOverride) return `Custom (${customMarkup || 0}%)`;
    switch (source) {
      case 'custom': return 'Custom';
      case 'category': return 'Category Tier';
      case 'global': return 'Company Default';
      case 'item_override': return 'Custom';
      case 'company_category_tier': return 'Category Tier';
      case 'system_default_tier': return 'Platform Default Tier';
      case 'company_default': return 'Company Default';
      case 'system_default': return 'Platform Default';
      default: return source || 'Unknown';
    }
  };

  const getFinalPrice = (item: LineItem) => {
    if (item.final_price !== null && item.final_price !== undefined) {
      return item.final_price;
    }
    // Don't calculate fallback - show that no final price is stored
    return null;
  };

  const handleEditClick = () => {
    if (selectedItem) {
      navigate(`/cost-center/internal-line-items/edit/${selectedItem.id}`);
      onClose();
    }
  };

  const sourcesQuery = useQuery({
    queryKey: ['dialog-sources', selectedItem?.id, selectedItem?.item_type],
    enabled: !!selectedItem?.id,
    queryFn: async () => {
      if (!selectedItem?.id) return [];

      if (selectedItem.item_type === 'system') {
        const { data, error } = await (supabase as any)
          .from('system_line_item_price_sources')
          .select(`
            sort_order,
            source_name,
            platform_supplier_id,
            platform_suppliers!platform_supplier_id(name)
          `)
          .eq('system_line_item_id', selectedItem.id)
          .order('sort_order');
        if (error) return [];
        return ((data ?? []) as any[]).map((r) => ({
          sort_order: r.sort_order,
          display_name: r.platform_suppliers?.name ?? r.source_name ?? null,
        })).filter((r) => !!r.display_name);
      } else {
        const { data, error } = await (supabase as any)
          .from('internal_line_item_price_sources')
          .select(`
            sort_order,
            source_name,
            platform_supplier_id,
            supplier_id,
            platform_suppliers!platform_supplier_id(name),
            suppliers!supplier_id(name)
          `)
          .eq('internal_line_item_id', selectedItem.id)
          .order('sort_order');
        if (error) return [];
        return ((data ?? []) as any[]).map((r) => ({
          sort_order: r.sort_order,
          display_name:
            r.platform_suppliers?.name ??
            r.suppliers?.name ??
            r.source_name ??
            null,
        })).filter((r) => !!r.display_name);
      }
    },
  });

  const { data: staleThresholds } = useQuery({
    queryKey: ['platform_stale_thresholds'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_platform_stale_thresholds');
      if (error) throw error;
      return data as { amber_days: number; red_days: number };
    },
    staleTime: 5 * 60 * 1000,
  });
  const amberDays = staleThresholds?.amber_days ?? 90;
  const redDays   = staleThresholds?.red_days   ?? 180;

  return (
    <Dialog open={!!selectedItem} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <span>{selectedItem?.name || selectedItem?.title || 'Line Item Details'}</span>
            {selectedItem?.item_type === 'system' && (
              <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100">
                System
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>View the details of this line item</DialogDescription>
        </DialogHeader>
        
        <div className="max-h-[60vh] overflow-y-auto">
          {selectedItem && (
            <div className="space-y-4 px-6">
              {/* Image Gallery Section */}
              {selectedItem.image_urls && selectedItem.image_urls.length > 0 ? (
                <div className="flex gap-2 overflow-x-auto py-2">
                  {selectedItem.image_urls.map((url, i) => (
                    <img 
                      key={i}
                      src={url} 
                      alt={`${selectedItem.title} image ${i+1}`}
                      className="h-40 w-auto object-cover rounded-md"
                      onError={handleImageError}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground italic">No images available</div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                  <p className="break-words">{selectedItem.description || 'No description'}</p>
                </div>
                
                <div className="col-span-2 grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Base Unit</h4>
                    <p className="font-medium">{getUnitLabel(selectedItem.base_unit)}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Base Cost</h4>
                    <p className="font-medium">{formatCurrency(selectedItem.base_cost)}</p>
                  </div>

                  {!hideMarkupSource && selectedItem.markup_percentage != null && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">Markup</h4>
                      <p className="font-medium">{Number(selectedItem.markup_percentage).toFixed(2)}%</p>
                    </div>
                  )}

                  <div className={!hideMarkupSource && selectedItem.markup_percentage != null ? '' : 'col-span-2'}>
                    <h4 className="text-sm font-medium text-muted-foreground">Final Price</h4>
                    {getFinalPrice(selectedItem) !== null ? (
                      <p className="font-semibold text-green-600">{formatCurrency(getFinalPrice(selectedItem)!)}</p>
                    ) : (
                      <p className="font-semibold text-red-600">Not calculated - pricing may be stale</p>
                    )}
                  </div>
                </div>

                {!hideMarkupSource && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Pricing Source</h4>
                    <Badge variant={getPricingSourceBadgeVariant(selectedItem.markup_source, selectedItem.override_markup)}>
                      {getPricingSourceLabel(selectedItem.markup_source, selectedItem.override_markup, selectedItem.custom_markup)}
                    </Badge>
                  </div>
                )}

                {/* Markup override — system items only */}
                {selectedItem.item_type === 'system' && (
                  <div className="col-span-2 rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">Custom Markup Override</div>
                        <div className="text-xs text-muted-foreground">
                          Override the platform markup for this specific item
                        </div>
                      </div>
                      <Switch
                        checked={!!selectedItem.override_markup}
                        onCheckedChange={() => onToggleSystemOverride?.()}
                      />
                    </div>
                    {selectedItem.override_markup && (
                      <div className="mt-3 flex items-center gap-2">
                        <Input
                          type="number"
                          value={systemOverrideMarkup ?? selectedItem.custom_markup ?? 0}
                          onChange={(e) =>
                            setSystemOverrideMarkup?.(parseFloat(e.target.value) || 0)
                          }
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <Button type="button" size="sm" onClick={() => onSaveSystemOverride?.()}>
                          Save
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onClearSystemOverride?.()}
                        >
                          Clear Override
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {lastSeenPrice != null && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Last seen at this supplier</h4>
                    <p className="font-semibold">
                      R {Number(lastSeenPrice).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}

                {/* Browse Category — system items */}
                {selectedItem.browse_category_name && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Browse Category</h4>
                    <p>{selectedItem.browse_category_name}</p>
                  </div>
                )}

                {/* Price last updated — system items */}
                {selectedItem.price_last_updated_at && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Price Last Updated</h4>
                    <p className={cn(
                      'text-sm',
                      (() => {
                        const ageMs = Date.now() - new Date(selectedItem.price_last_updated_at!).getTime();
                        if (ageMs > redDays * 24 * 60 * 60 * 1000) return 'text-red-600';
                        if (ageMs > amberDays * 24 * 60 * 60 * 1000) return 'text-amber-600';
                        return '';
                      })()
                    )}>
                      {formatDistanceToNow(
                        new Date(selectedItem.price_last_updated_at),
                        { addSuffix: true }
                      )}
                    </p>
                  </div>
                )}

                {/* Notes — system items */}
                {selectedItem.notes && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
                    <p className="break-words">{selectedItem.notes}</p>
                  </div>
                )}

                {/* Price Sources — read-only, names only */}
                {sourcesQuery.data && sourcesQuery.data.length > 0 && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">{priceSourcesLabel ?? 'Price Sources'}</h4>
                    <p>{sourcesQuery.data.map((s) => s.display_name).join(' · ')}</p>
                  </div>
                )}

                {/* Unit Conversion Section */}
                {selectedItem.conversion_unit && selectedItem.conversion_factor && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Unit Conversion</h4>
                    <p>1 {getUnitLabel(selectedItem.conversion_unit)} = {selectedItem.conversion_factor} {getUnitLabel(selectedItem.base_unit)}</p>
                  </div>
                )}
                
                {/* Productivity Section */}
                {selectedItem.productivity_unit && selectedItem.productivity_rate && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Productivity</h4>
                    <p>{selectedItem.productivity_rate} {getUnitLabel(selectedItem.base_unit)} per {getUnitLabel(selectedItem.productivity_unit)}</p>
                  </div>
                )}

                {/* Categories & Trades Section */}
                <div className="col-span-2">
                  <div className="space-y-4">
                    {(selectedItem.category || selectedItem.trade) ? (
                      <>
                        {selectedItem.category && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Category</h4>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="outline">{selectedItem.category}</Badge>
                            </div>
                          </div>
                        )}
                        
                        {selectedItem.trade && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Trade</h4>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="outline">{selectedItem.trade}</Badge>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted-foreground text-sm italic">No category or trade assigned</div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Tags Section */}
              {selectedItem.tags && selectedItem.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Tags</h4>
                  {renderTags(selectedItem.tags)}
                </div>
              )}

              {/* Display pricing tiers */}
              {selectedItem.internal_pricing_tiers && selectedItem.internal_pricing_tiers.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Bulk Pricing Tiers</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quantity Range</TableHead>
                        <TableHead>Unit Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItem.internal_pricing_tiers
                        .sort((a, b) => a.qty_min - b.qty_min)
                        .map((tier, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {tier.qty_min}+ {tier.qty_max ? ` to ${tier.qty_max}` : ''}
                            </TableCell>
                            <TableCell>{formatCurrency(tier.unit_cost)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="px-6 pt-4 pb-6">
          {(!selectedItem?.item_type || selectedItem.item_type === 'internal') && (
            <Button type="button" onClick={handleEditClick}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Line Item
            </Button>
          )}
          {selectedItem?.item_type === 'system' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" />
              Managed by Doyence
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LineItemDetailsDialog;
